import React from "react";
import {
  Box,
  Typography,
  CardContent,
  Grid,
  CircularProgress,
  Divider,
} from "@mui/material";
import { StyledCard } from "../../common/StyledCard";
import { formatCurrency } from "../../../utils/currencyUtils";
import { QuoteConversionFunnelBiDto } from "../../../shared/dtos/bi/QuoteConversionFunnelBiDto";

interface QuoteConversionFunnelWidgetProps {
  data?: QuoteConversionFunnelBiDto | null;
}

const QuoteConversionFunnelWidget: React.FC<
  QuoteConversionFunnelWidgetProps
> = ({ data }) => {
  if (!data) {
    return (
      <StyledCard
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <CardContent
          sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
            Quote Conversion Funnel
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 300,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No quote conversion data available.
            </Typography>
          </Box>
        </CardContent>
      </StyledCard>
    );
  }

  const {
    totalQuotes,
    convertedQuotes,
    expiredUnconvertedQuotes,
    totalQuotedValue,
    conversionRatePercentage,
  } = data;

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
          Quote Conversion Funnel
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              variant="determinate"
              value={100}
              size={120}
              thickness={4}
              sx={{ color: "action.hover" }}
            />

            <CircularProgress
              variant="determinate"
              value={conversionRatePercentage}
              size={120}
              thickness={4}
              color="success"
              sx={{
                position: "absolute",
                left: 0,
              }}
            />

            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  color: "success.main",
                }}
              >
                {conversionRatePercentage}%
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Conversion
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2} sx={{ textAlign: "center" }}>
          <Grid size={{ xs: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Total Quotes
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {totalQuotes}
            </Typography>
          </Grid>

          <Grid size={{ xs: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Converted
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "success.main",
              }}
            >
              {convertedQuotes}
            </Typography>
          </Grid>

          <Grid size={{ xs: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Expired
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "error.main",
              }}
            >
              {expiredUnconvertedQuotes}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
            <Box
              sx={{
                p: 1.5,
                bgcolor: "primary.light",
                color: "primary.contrastText",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  opacity: 0.9,
                }}
              >
                Total Quoted Value in Pipeline
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {formatCurrency(totalQuotedValue)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );
};

export default QuoteConversionFunnelWidget;
